import instance from "@/plugins/axios";
import {
  AbstractEventsRemoteDataSource,
  CreateSubEventParams,
  EditEventParams,
  GetEventsParams,
  GetGeneralInformationParams,
} from "@/modules/events/remote/AbstractEventsRemoteDataSource";
import { ResponseResult } from "@/core/models/response-result";
import { EventModel } from "@/modules/events/models/event-model";
import { EventState } from "@/modules/events/models/event-state";
import { EventGeneralInformation } from "@/modules/events/models/event-general-information";
import { EventDetail } from "../models/event-detail";
import { EventRequirement } from "../models/event-requirement";
import { ResourceRemoteDataSource } from "@/modules/resources/remote/ResourceRemoteDataSource";
import { SubEvent } from "../models/sub-event";
import { TableState } from "@/core/models/table-state";
import { OccupancyResource } from "@/core/models/occupancy-resource";

export class EventsRemoteDataSource implements AbstractEventsRemoteDataSource {
  async getEvents(params?: GetEventsParams): Promise<ResponseResult> {
    try {
      const response = (
        await instance.get<ResponseResult<any>>("Events", { params })
      ).data;
      if (!response.isSuccess) return response;
      response.data = this.convertFromJsonToTableState(response.data);
      return response;
    } catch (error) {
      console.error(error);
      return ResponseResult.defaultError(error);
    }
  }

  async getEvent(id: string): Promise<ResponseResult> {
    try {
      const response = (await instance.get<ResponseResult<any>>("Events/" + id))
        .data;
      if (!response.isSuccess) return response;
      response.data = this.convertFromJsonToEventDetail(response.data);
      return response;
    } catch (error) {
      console.error(error);
      return ResponseResult.defaultError(error);
    }
  }

  async editEvent(
    id: string,
    params: EditEventParams,
  ): Promise<ResponseResult> {
    try {
      const response = (
        await instance.put<ResponseResult<any>>("Events/" + id, params)
      ).data;
      if (!response.isSuccess) return response;
      response.data = this.convertFromJsonToEventDetail(response.data);
      return response;
    } catch (error) {
      console.error(error);
      return ResponseResult.defaultError(error);
    }
  }

  async getGeneralInformation(
    params: GetGeneralInformationParams,
  ): Promise<ResponseResult> {
    try {
      const response = (
        await instance.get<ResponseResult<any>>("Events/dashboard", { params })
      ).data;
      if (!response.isSuccess) return response;
      response.data = this.convertFromJsonToEventGeneralInformation(
        response.data,
      );
      return response;
    } catch (error) {
      console.error(error);
      return ResponseResult.defaultError(error);
    }
  }

  async createSubEvent(
    id: string,
    params: CreateSubEventParams,
  ): Promise<ResponseResult> {
    try {
      const response = (
        await instance.post<ResponseResult<any>>(
          "Events/" + id + "/subevents",
          params,
        )
      ).data;
      if (!response.isSuccess) return response;
      return response;
    } catch (error) {
      console.error(error);
      return ResponseResult.defaultError(error);
    }
  }

  async getSubEvents(id: string): Promise<ResponseResult> {
    try {
      const response = (
        await instance.get<ResponseResult<any>>("Events/" + id + "/subevents")
      ).data;
      if (!response.isSuccess) return response;
      response.data = this.convertFromJsonToSubEventList(response.data);
      return response;
    } catch (error) {
      console.error(error);
      return ResponseResult.defaultError(error);
    }
  }

  async editSubEvent(
    eventId: string,
    subEventId: number,
    params: CreateSubEventParams,
  ): Promise<ResponseResult> {
    try {
      const response = (
        await instance.put<ResponseResult<any>>(
          "Events/" + eventId + "/subevents/" + subEventId,
          params,
        )
      ).data;
      if (!response.isSuccess) return response;
      return response;
    } catch (error) {
      console.error(error);
      return ResponseResult.defaultError(error);
    }
  }

  async removeResourceFromEvent(
    eventId: string,
    resourceId: number,
  ): Promise<ResponseResult> {
    try {
      const response = (
        await instance.delete<ResponseResult<any>>(
          "Resources/events/" + eventId + "/allocations/" + resourceId,
        )
      ).data;
      return response;
    } catch (error) {
      console.error(error);
      return ResponseResult.defaultError(error);
    }
  }

  async deleteEvent(id: string): Promise<ResponseResult> {
    try {
      const response = (
        await instance.put<ResponseResult<any>>("Events/cancel/" + id)
      ).data;
      return response;
    } catch (error) {
      console.error(error);
      return ResponseResult.defaultError(error);
    }
  }

  async getEventsTypes(): Promise<ResponseResult> {
    try {
      const response = (await instance.get<ResponseResult<any>>("Events/types"))
        .data;
      return response;
    } catch (error) {
      console.error(error);
      return ResponseResult.defaultError(error);
    }
  }

  async downloadPDF(id: string): Promise<ResponseResult> {
    try {
      const response = (
        await instance.post<ResponseResult<any>>(
          "Events/" + id + "/download",
          null,
          {
            responseType: "blob",
          },
        )
      ).data;
      return response;
    } catch (error) {
      console.error(error);
      return ResponseResult.defaultError(error);
    }
  }

  convertFromJsonToTableState(data: any): TableState<EventModel> {
    return {
      pageNumber: data.pageNumber,
      pageSize: data.pageSize,
      totalItems: data.totalItems,
      totalPages: data.totalPages,
      items: this.convertFromJsonToEventModelList(data.items),
    };
  }

  convertFromJsonToEventGeneralInformation(data: any): EventGeneralInformation {
    return {
      numberOfNotStartedEvents: data.numberOfNotStartedEvents,
      numberOfInPreparation: data.numberOfInPreparationEvents,
      numberOfPrepared: data.numberOfPreparedEvents,
      numberOfInExecution: data.numberOfInExecutionEvents,
      numberOfConcluded: data.numberOfConcludedEvents,
      numberOfCanceled: data.numberOfCanceledEvents,
    };
  }

  convertFromJsonToEventModelList(data: any): EventModel[] {
    return data.map((item: any) => this.convertFromJsonToEventModel(item));
  }

  convertFromJsonToEventModel(data: any): EventModel {
    return {
      id: data.eventId,
      name: data.name,
      type: data.type,
      creationDate: data.creationDate,
      startDate: new Date(data.startDate),
      endDate: new Date(data.endDate),
      location: data.location,
      numberOfParticipants: data.numberOfParticipants,
      status: EventState.getEventStateByValue(data.status),
    };
  }

  convertFromJsonToEventDetail(data: any): EventDetail {
    return {
      id: data.eventId,
      name: data.name,
      type: data.type, //TODO:REVER
      status: EventState.getEventStateByValue(data.status),
      creationDate: new Date(data.creationDate),
      startDate: new Date(data.startDate),
      endDate: new Date(data.endDate),
      location: data.location,
      numberOfParticipants: data.numberOfParticipants,
      budget: data.budget,
      description: data.description,
      requestingService: data.requestingService,
      team: data.team,
      venueType: data.venueType, //TODO:REVER
      expense: data.moneySpent,
      resourceTypes: ResourceRemoteDataSource.convertFromJsonToResourceTypeList(
        data.types,
      ),
      requirements: this.convertFromJsonToRequirementList(data.requirements),
      subEvents: this.convertFromJsonToSubEventList(data.subEvents),
      occupancyCalendarResources: this.convertFromJsonToOccupancyResourceList(
        data.occupancyCalendar?.resources ?? [],
      ),
    };
  }

  convertFromJsonToRequirementList(data: any[]): EventRequirement[] {
    return data.map((item: any) => this.convertFromJsonToRequirement(item));
  }

  convertFromJsonToRequirement(data: any): EventRequirement {
    return {
      id: data.id,
      eventId: data.eventId,
      requiredQuantity: data.requiredQuantity,
      resourceType: data.resourceType,
      state: data.status,
      resourceSubType: data.resourceSubType,
    };
  }

  convertFromJsonToSubEventList(data: any): SubEvent[] {
    return data.map((item: any) => this.convertFromJsonToSubEvent(item));
  }

  convertFromJsonToSubEvent(data: any): SubEvent {
    return {
      id: data.subEventId,
      name: data.name,
      startDate: new Date(data.startDate),
      endDate: new Date(data.endDate),
      location: data.location,
      resourceTypes: ResourceRemoteDataSource.convertFromJsonToResourceTypeList(
        data.types,
      ),
    };
  }

  convertFromJsonToOccupancyResourceList(data: any[]): OccupancyResource[] {
    return data.map((item: any) =>
      this.convertFromJsonToOccupancyResource(item),
    );
  }

  convertFromJsonToOccupancyResource(data: any): OccupancyResource {
    return {
      resourceId: data.resourceId,
      name: data.name,
      intervals: data.intervals.map((interval: any) => {
        return {
          quantity: interval.quantity,
          eventId: interval.eventId,
          name: interval.name,
          bars: interval.bars.map((bar: any) => {
            return {
              startDate: bar.startDate,
              endDate: bar.endDate,
            };
          }),
        };
      }),
    };
  }
}
